import { createElement, useEffect, useRef, useState } from "react";
import { useOnline } from "@/hooks/useOnline";
import { ApiError, type RequestOptions } from "@/api/client";
import type { RunHeat, RunRider } from "@/components/run/model";
export interface OfficialTransport {
  get: <T>(path: string) => Promise<T>;
  post: <T>(path: string, body?: unknown, options?: RequestOptions<T>) => Promise<T>;
  put: <T>(path: string, body?: unknown, options?: RequestOptions<T>) => Promise<T>;
}
export interface OfficialSheetProps {
  competitionId: string;
  heat: RunHeat;
  transport: OfficialTransport;
  onClose: () => void;
  onSaved: () => Promise<void>;
  onUndoAvailable?: (ticket: { eventId: string; label: string }) => void;
}
export interface RiderSheetProps extends OfficialSheetProps {
  rider: RunRider;
}
export function useOfficialWrite(props: OfficialSheetProps) {
  const online = useOnline();
  const [ticket, setTicket] = useState<{ eventId: string; label: string } | null>(null);
  const hasUndo = useRef(false);
  const intentKey = useRef<{ signature: string; key: string } | null>(null);
  useEffect(() => {
    if (!ticket) return;
    const timer = setTimeout(() => setTicket(null), 5000);
    return () => clearTimeout(timer);
  }, [ticket]);
  async function execute<T = unknown>(kind: string, payload: unknown): Promise<T> {
    const signature = JSON.stringify([kind, payload]);
    if (intentKey.current?.signature !== signature)
      intentKey.current = { signature, key: crypto.randomUUID() };
    const response = await props.transport.post<{ result: T; undo_event_id: string | null }>(
      `/api/competitions/${props.competitionId}/official-commands`,
      { heat_id: props.heat.id, kind, payload },
      { idempotencyKey: intentKey.current.key },
    );
    if (
      !response ||
      !("result" in response) ||
      !(response.undo_event_id === null || typeof response.undo_event_id === "string")
    )
      throw new Error("Command receipt was invalid. Reload this screen before retrying.");
    if (response.undo_event_id) {
      hasUndo.current = true;
      const next = { eventId: response.undo_event_id, label: `${kind.replaceAll("_", " ")} saved` };
      if (props.onUndoAvailable) props.onUndoAvailable(next);
      else setTicket(next);
    }
    return response.result;
  }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [needsRefresh, setNeedsRefresh] = useState(false);
  async function write(command: () => Promise<unknown>, close = true) {
    if (!navigator.onLine || busy || needsRefresh) return;
    setBusy(true);
    hasUndo.current = false;
    setError("");
    try {
      await command();
      try {
        await props.onSaved();
      } catch {
        setNeedsRefresh(true);
        setError("Saved. Could not refresh this screen. Close this sheet and reload.");
        return;
      }
      if (close && (!hasUndo.current || props.onUndoAvailable)) props.onClose();
    } catch (e) {
      setError(
        e instanceof ApiError && e.code === "official_posting"
          ? "Correct via Desk"
          : e instanceof Error
            ? e.message
            : "Could not save. Your entry has been kept.",
      );
    } finally {
      setBusy(false);
    }
  }
  const undo = ticket
    ? createElement(
        "p",
        { role: "status", className: "run-banner" },
        ticket.label,
        " ",
        createElement(
          "button",
          {
            type: "button",
            disabled: busy || !online,
            onClick: () => {
              const eventId = ticket.eventId;
              setTicket(null);
              void write(
                () =>
                  props.transport.post(
                    `/api/competitions/${props.competitionId}/undo`,
                    { event_id: eventId },
                    { idempotencyKey: crypto.randomUUID() },
                  ),
                true,
              );
            },
          },
          "Undo",
        ),
      )
    : null;
  return { busy: busy || !online || needsRefresh, error, write, execute, undo };
}
